import { InlineForm, InlineOptions, InlineValueOrType } from "./inline-form";
import {
  FormFieldSettings,
  CombinedFormFieldSetting,
  ensureDefaultFormComponents
} from "../../form-settings/form-field-settings";
import { FormComponentsByType, isDynamicFieldType, DynamicFormType } from "../../form-field/form-field-type";
import { FormFieldSetting } from "../../form-settings/form-field-setting";
import { SubType } from "../../form-settings/sub-type";
import { ArrayFormFieldSetting } from "../../form-field/array/array-form-field-setting";
import { ArrayRenderStrategy } from "../../form-field/array/array-render-strategy";
import { InlineSetting } from "../../form-settings/inline-setting";
import { FormFieldComponentType } from "../../form-field/form-field-component";
import { FormFieldArrayComponent } from "../../form-field/array/form-field-array-component";
import { Type } from "@angular/core";

type PartialInlineOptions<S = FormFieldSetting> = [Partial<S>, InlineValueOrType, FormFieldComponentType?];
type SettingMapEntry = [string, CombinedFormFieldSetting];
type SettingResolverResult = { match: boolean; value?: SettingMapEntry[] };
type SettingResolver = (
  options: PartialInlineOptions,
  componentResolver: (setting: Partial<FormFieldSetting>, comp: FormFieldComponentType) => FormFieldComponentType,
  factory: (options: any) => SettingMapEntry[]
) => SettingResolverResult;

const MissingSettingResolver: SettingResolver = ([setting]: PartialInlineOptions) => {
  throw new Error(`Unssuported form field for ${setting.name}`);
};

const ComponentSettingResolver: SettingResolver = ([setting, form]: PartialInlineOptions) => {
  if (typeof form !== "function") {
    return {
      match: false
    };
  }
  setting.component = form;

  return {
    match: true,
    value: [[setting.name, setting as CombinedFormFieldSetting]]
  };
};

const DynamicTypeSettingResolver: SettingResolver = ([setting, form, optionalComponent], componentResolver) => {
  if (!isDynamicFieldType(form) && !(form instanceof SubType)) {
    return {
      match: false
    };
  }

  if (form !== DynamicFormType.HIDDEN && !(form instanceof SubType && form.type === DynamicFormType.HIDDEN)) {
    setting.component = componentResolver(setting, optionalComponent);
  }

  setting.type = form;

  return {
    match: true,
    value: [[setting.name, setting as CombinedFormFieldSetting]]
  };
};

const ArraySettingResolver: SettingResolver = (
  [setting, form, optionalComponent]: PartialInlineOptions<ArrayFormFieldSetting>,
  componentResolver,
  factory
) => {
  if (!Array.isArray(form)) {
    return {
      match: false
    };
  }

  setting.type = DynamicFormType.ARRAY;
  setting.component = componentResolver(setting, optionalComponent) as Type<FormFieldArrayComponent>;
  setting.renderStrategy = setting.renderStrategy || ArrayRenderStrategy.withFullRender();

  const [[, childSetting], ...indirectChildren] = factory(form);
  setting.childSetting = childSetting;

  return {
    match: true,
    value: [[setting.name, setting as CombinedFormFieldSetting], ...indirectChildren]
  };
};

const GroupSettingResolver: SettingResolver = (
  [setting, form, optionalComponent]: PartialInlineOptions,
  componentResolver,
  factory
) => {
  if (typeof form !== "object") {
    return {
      match: false
    };
  }

  const children = Object.entries(form).reduce<SettingMapEntry[]>((settings, [id, options]) => {
    const [[, childSetting], ...indirectChildren] = factory(options);
    return [...settings, [id, childSetting], ...indirectChildren];
  }, []);

  // form root does not have a name
  if (!setting.name) {
    return {
      match: true,
      value: children
    };
  }

  setting.type = DynamicFormType.GROUP;
  setting.component = componentResolver(setting, optionalComponent);

  return {
    match: true,
    value: [[setting.name, setting as CombinedFormFieldSetting], ...children]
  };
};

export class InlineSettingsResolver {
  private readonly resolvers: SettingResolver[] = [
    DynamicTypeSettingResolver,
    ComponentSettingResolver,
    ArraySettingResolver,
    GroupSettingResolver,
    MissingSettingResolver
  ];
  constructor(private defaultFormComponents?: FormComponentsByType) {}

  public resolve(inlineForm: InlineForm): FormFieldSettings {
    return new Map(this.resolveSettings([null, inlineForm]));
  }

  private resolveSettings([nameOrSetting, form, optionalComponent]: InlineOptions): SettingMapEntry[] {
    let setting: Partial<FormFieldSetting> = this.createPartialSetting(nameOrSetting);
    return this.resolvers.reduce<SettingResolverResult>(
      (prev, curr) =>
        prev.match
          ? prev
          : curr(
              [setting, form, optionalComponent],
              this.componentResolver.bind(this),
              this.resolveSettings.bind(this)
            ),
      {
        match: false,
        value: []
      }
    ).value;
  }

  private createPartialSetting(nameOrSetting: string | InlineSetting<any>): Partial<FormFieldSetting> {
    let setting: Partial<FormFieldSetting> = {};
    if (typeof nameOrSetting === "string") {
      setting.name = nameOrSetting;
    } else {
      setting = nameOrSetting || {};
    }

    return setting;
  }

  private componentResolver(setting: Partial<FormFieldSetting>, optionalComponent: FormFieldComponentType) {
    return optionalComponent || ensureDefaultFormComponents(this.defaultFormComponents, setting.name, setting.type);
  }
}
